import type { DragEvent } from 'react';
import React, { useCallback, useRef, useState } from 'react';
import { toast } from 'react-toastify';

import { CloudUploadOutlined, FolderOpenOutlined } from '@ant-design/icons';
import { Button, Spin } from 'antd';

export interface IImageReaderResult {
  url: string;
  width: number;
  height: number;
}

interface IProps {
  droppable?: boolean;
  className?: string;
  onLoad: (result: IImageReaderResult) => void;
}

const ALLOWED_EXTENSIONS = ['png', 'gif', 'jpeg', 'jpg', 'bmp'];
const ACCEPT_STRING = `.${ALLOWED_EXTENSIONS.join(',.')}`;
const EXTENSION_REGEXP = new RegExp(`.(${ALLOWED_EXTENSIONS.join('|')})$`, 'gi');
const IMAGE_LOAD_DELAY_MS = 1000;
const IMAGE_LOAD_ERROR = 'Não conseguimos carregar a imagem';

const ImageReader = ({ droppable, className, onLoad }: IProps) => {
  const [loading, setLoading] = useState(false);
  const [draggingOver, setDraggingOver] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const onLoadRef = useRef(onLoad);
  onLoadRef.current = onLoad;

  const getImageDimensions = useCallback((url: string) => {
    const image = new Image();

    image.onload = () => {
      setTimeout(() => {
        setLoading(false);
        onLoadRef.current({ url, width: image.width, height: image.height });
      }, IMAGE_LOAD_DELAY_MS);
    };

    image.onerror = () => {
      toast.error(IMAGE_LOAD_ERROR);
      setLoading(false);
    };

    image.src = url;
  }, []);

  const loadFile = useCallback(
    (file: File) => {
      if (!EXTENSION_REGEXP.test(file.name)) {
        EXTENSION_REGEXP.lastIndex = 0;
        toast.error(`Apenas imagens: ${ALLOWED_EXTENSIONS.join(', ')}`);
        return;
      }

      EXTENSION_REGEXP.lastIndex = 0;

      const reader = new FileReader();
      reader.onload = (e: any) => getImageDimensions(e.target.result);

      reader.onerror = () => {
        toast.error(IMAGE_LOAD_ERROR);
        setLoading(false);
      };

      reader.readAsDataURL(file);
    },
    [getImageDimensions]
  );

  const handleSelectImage = useCallback(() => {
    inputRef.current?.click();
  }, []);

  const onFileSelected = useCallback(() => {
    if (!inputRef.current?.files?.length) {
      return;
    }

    setLoading(true);
    loadFile(inputRef.current.files[0]);
    inputRef.current.value = '';
  }, [loadFile]);

  const onDropFile = useCallback(
    (event: DragEvent<any>) => {
      event.preventDefault();
      setDraggingOver(false);

      if (loading) {
        return;
      }

      loadFile(event.dataTransfer.files[0]);
    },
    [loading, loadFile]
  );

  const onDragIn = useCallback(
    (event: DragEvent<any>) => {
      event.preventDefault();

      if (loading || draggingOver) {
        return;
      }

      setDraggingOver(true);
    },
    [loading, draggingOver]
  );

  const onDragOut = useCallback(
    (event: DragEvent<any>) => {
      event.preventDefault();

      if (loading || !draggingOver) {
        return;
      }

      setDraggingOver(false);
    },
    [loading, draggingOver]
  );

  const renderButton = () => (
    <>
      <input
        type='file'
        ref={inputRef}
        style={{ display: 'none' }}
        onChange={onFileSelected}
        accept={ACCEPT_STRING}
        id='upload-new-image-file-input'
      />

      <Button
        disabled={loading}
        loading={loading}
        icon={<FolderOpenOutlined />}
        onClick={handleSelectImage}
        style={{ color: '#333' }}
        id='upload-new-image-button'
      >
        Selecione sua imagem
      </Button>
    </>
  );

  if (!droppable) {
    return renderButton();
  }

  const dropAreaBase =
    'flex max-h-full min-h-[150px] w-full flex-col items-center justify-center rounded-sm text-center text-[#333]';
  const dropDraggingChildren =
    'pointer-events-none mb-2 flex flex-col items-center justify-center text-[15px] font-medium';

  return (
    <div
      className={`${dropAreaBase} ${className || ''} ${draggingOver ? 'bg-[#fff10a1f]' : ''}`}
      onDrop={onDropFile}
      onDragOver={onDragIn}
      onDragLeave={onDragOut}
    >
      {loading && <Spin size='large' className='mt-0' />}

      {!loading && (
        <>
          <div className={dropDraggingChildren}>
            <CloudUploadOutlined className='text-[36px]' />
            Arraste sua imagem aqui ou
          </div>

          <div className={draggingOver ? dropDraggingChildren : undefined}>{renderButton()}</div>
        </>
      )}
    </div>
  );
};

export default ImageReader;
